// @flow
import * as ActionTypes from "constants/ActionTypes";

type WebsiteScanType = {
    _id: string,
    target_url: string,
    domain: string,
    status: 'pending' | 'scanning' | 'completed' | 'failed' | 'cancelled',
    progress: {
        pages_scanned: number,
        products_found: number,
        products_saved: number,
        percent_complete: number,
        current_page?: string
    },
    results?: {
        total_products: number,
        new_products: number,
        updated_products: number,
        json_ld_found: number,
        microdata_found: number,
        rdfa_found: number
    },
    started_at?: string,
    completed_at?: string,
    duration_ms?: number
};

type ScrapedProductType = {
    _id: string,
    source_domain: string,
    product_url: string,
    name: string,
    brand?: string,
    price?: {
        value: number,
        currency: string
    },
    images?: Array<{url: string, type: string}>,
    structured_data: {
        json_ld: Array<any>,
        microdata: Array<any>,
        rdfa: Array<any>
    }
};

type DomainStatsType = {
    domain: string,
    product_count: number,
    brand_count: number,
    last_scraped: string
};

type StateType = {
    loading: boolean,
    error: ?string,
    
    currentScan: ?WebsiteScanType,
    activeScans: Array<WebsiteScanType>,
    scanHistory: Array<WebsiteScanType>,
    
    scrapedProducts: Array<ScrapedProductType>,
    currentProduct: ?ScrapedProductType,
    productsPagination: {
        total: number,
        page: number,
        limit: number,
        total_pages: number
    },
    
    scannedDomains: Array<DomainStatsType>,
    
    quickScanResult: ?any,
    
    pollingIntervals: { [scanId: string]: any }
};

const initialState: StateType = {
    loading: false,
    error: null,
    
    currentScan: null,
    activeScans: [],
    scanHistory: [],
    
    scrapedProducts: [],
    currentProduct: null,
    productsPagination: {
        total: 0,
        page: 1,
        limit: 50,
        total_pages: 0
    },
    
    scannedDomains: [],
    
    quickScanResult: null,
    
    pollingIntervals: {}
};

const adminWebsiteScraperReducer = (state: StateType = initialState, action: any): StateType => {
    switch (action.type) {
        // Start scan
        case ActionTypes.ADMIN_START_WEBSITE_SCAN_START:
            return {
                ...state,
                loading: true,
                error: null
            };
            
        case ActionTypes.ADMIN_START_WEBSITE_SCAN_SUCCESS:
            return {
                ...state,
                loading: false,
                currentScan: action.payload.response,
                activeScans: [action.payload.response, ...state.activeScans]
            };
            
        case ActionTypes.ADMIN_START_WEBSITE_SCAN_FAILURE:
            return {
                ...state,
                loading: false,
                error: action.error
            };
        
        // Quick scan
        case ActionTypes.ADMIN_QUICK_SCAN_START:
            return {
                ...state,
                loading: true,
                error: null,
                quickScanResult: null
            };
            
        case ActionTypes.ADMIN_QUICK_SCAN_SUCCESS:
            return {
                ...state,
                loading: false,
                quickScanResult: action.payload.response
            };
            
        case ActionTypes.ADMIN_QUICK_SCAN_FAILURE:
            return {
                ...state,
                loading: false,
                error: action.error
            };
        
        // Get scan status
        case ActionTypes.ADMIN_GET_SCAN_STATUS_START:
            return {
                ...state,
                loading: true,
                error: null
            };
            
        case ActionTypes.ADMIN_GET_SCAN_STATUS_SUCCESS: {
            const updatedScan = action.payload.response;
            return {
                ...state,
                loading: false,
                currentScan: updatedScan,
                activeScans: state.activeScans.map(scan => 
                    scan._id === updatedScan._id ? updatedScan : scan
                )
            };
        }
            
        case ActionTypes.ADMIN_GET_SCAN_STATUS_FAILURE:
            return {
                ...state,
                loading: false,
                error: action.error
            };
        
        // Get scan results
        case ActionTypes.ADMIN_GET_SCAN_RESULTS_START:
            return {
                ...state,
                loading: true,
                error: null
            };
            
        case ActionTypes.ADMIN_GET_SCAN_RESULTS_SUCCESS:
            return {
                ...state,
                loading: false,
                currentScan: action.payload.response
            };
            
        case ActionTypes.ADMIN_GET_SCAN_RESULTS_FAILURE:
            return {
                ...state,
                loading: false,
                error: action.error
            };
        
        // Get scraped products
        case ActionTypes.ADMIN_GET_SCRAPED_PRODUCTS_START:
            return {
                ...state,
                loading: true,
                error: null
            };
            
        case ActionTypes.ADMIN_GET_SCRAPED_PRODUCTS_SUCCESS:
            return {
                ...state,
                loading: false,
                scrapedProducts: action.payload.response.products,
                productsPagination: action.payload.response.pagination
            };
            
        case ActionTypes.ADMIN_GET_SCRAPED_PRODUCTS_FAILURE:
            return {
                ...state,
                loading: false,
                error: action.error
            };
        
        // Get scanned domains
        case ActionTypes.ADMIN_GET_SCANNED_DOMAINS_START:
            return {
                ...state,
                loading: true,
                error: null
            };
            
        case ActionTypes.ADMIN_GET_SCANNED_DOMAINS_SUCCESS:
            return {
                ...state,
                loading: false,
                scannedDomains: action.payload.response
            };
            
        case ActionTypes.ADMIN_GET_SCANNED_DOMAINS_FAILURE:
            return {
                ...state,
                loading: false,
                error: action.error
            };
        
        // Get scan history
        case ActionTypes.ADMIN_GET_SCAN_HISTORY_START:
            return {
                ...state,
                loading: true,
                error: null
            };
            
        case ActionTypes.ADMIN_GET_SCAN_HISTORY_SUCCESS:
            return {
                ...state,
                loading: false,
                scanHistory: action.payload.response
            };
            
        case ActionTypes.ADMIN_GET_SCAN_HISTORY_FAILURE:
            return {
                ...state,
                loading: false,
                error: action.error
            };
        
        // Cancel scan
        case ActionTypes.ADMIN_CANCEL_SCAN_START:
            return {
                ...state,
                loading: true,
                error: null
            };
            
        case ActionTypes.ADMIN_CANCEL_SCAN_SUCCESS: {
            const cancelledScan = action.payload.response;
            return {
                ...state,
                loading: false,
                currentScan: cancelledScan,
                activeScans: state.activeScans.map(scan => 
                    scan._id === cancelledScan._id ? cancelledScan : scan
                )
            };
        }
            
        case ActionTypes.ADMIN_CANCEL_SCAN_FAILURE:
            return {
                ...state,
                loading: false,
                error: action.error
            };
        
        // Delete scraped products
        case ActionTypes.ADMIN_DELETE_SCRAPED_PRODUCTS_START:
            return {
                ...state,
                loading: true,
                error: null
            };
            
        case ActionTypes.ADMIN_DELETE_SCRAPED_PRODUCTS_SUCCESS:
            return {
                ...state,
                loading: false,
                scrapedProducts: state.scrapedProducts.filter(
                    product => product.source_domain !== action.domain
                ),
                scannedDomains: state.scannedDomains.filter(
                    domain => domain.domain !== action.domain
                )
            };
            
        case ActionTypes.ADMIN_DELETE_SCRAPED_PRODUCTS_FAILURE:
            return {
                ...state,
                loading: false,
                error: action.error
            };
        
        // Get scraped product by ID
        case ActionTypes.ADMIN_GET_SCRAPED_PRODUCT_START:
            return {
                ...state,
                loading: true,
                error: null
            };
            
        case ActionTypes.ADMIN_GET_SCRAPED_PRODUCT_SUCCESS:
            return {
                ...state,
                loading: false,
                currentProduct: action.payload.response
            };
            
        case ActionTypes.ADMIN_GET_SCRAPED_PRODUCT_FAILURE:
            return {
                ...state,
                loading: false,
                error: action.error
            };
        
        // Polling
        case ActionTypes.ADMIN_POLL_SCAN_STATUS:
            return {
                ...state,
                pollingIntervals: {
                    ...state.pollingIntervals,
                    [action.scanId]: action.intervalId
                }
            };
            
        case ActionTypes.ADMIN_STOP_POLLING_SCAN_STATUS: {
            const { [action.intervalId]: removed, ...remaining } = state.pollingIntervals;
            return {
                ...state,
                pollingIntervals: remaining
            };
        }
        
        default:
            return state;
    }
};

export default adminWebsiteScraperReducer;
